import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, Lock } from "lucide-react";
import { cityConfig } from "@/config/cityConfig";
import { useSettings } from "@/hooks/useSettings";

const BRIMO_LOGO = "https://customer-assets.emergentagent.com/job_c18fba98-66cc-4e36-b9db-e24324823938/artifacts/t6fqodcb_BRIMO_Logo_Schwarz.png";
const AWARD_LOGO = "https://customer-assets.emergentagent.com/job_c18fba98-66cc-4e36-b9db-e24324823938/artifacts/xshmmzta_IMMOPREISGEWINNER.png";

export default function Footer() {
  const year = new Date().getFullYear();
  const settings = useSettings();

  return (
    <footer className="bg-[#0d0d0d] border-t border-white/5" data-testid="footer">
      <div className="h-1 bg-[#E61D38]" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 md:col-span-1">
            <Link to="/" data-testid="footer-logo-link">
              <img
                src={BRIMO_LOGO}
                alt="BRIMO Immobilien NRW"
                className="h-12 w-auto mb-4"
                data-testid="footer-brimo-logo"
              />
            </Link>
            <p className="text-white font-bold text-[10px] uppercase tracking-[0.25em] mb-0.5">
              BRICKS &amp; MORTAR
            </p>
            <p className="text-gray-500 text-[9px] uppercase tracking-[0.2em] mb-5">Kreis Mettmann</p>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              Ihr professioneller Immobilienmakler im Kreis Mettmann. Fundierte Beratung. Erfolgreicher
              Verkauf.
            </p>
            <img
              src={AWARD_LOGO}
              alt="Deutscher Immobilienpreis Winner 2023 – immowelt"
              className="h-20 w-auto border border-white/10"
              data-testid="footer-award-logo"
            />
          </div>

          {/* Leistungen */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-5">
              Leistungen
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Immobilie verkaufen", href: "/immobilie-verkaufen-kreis-mettmann" },
                { label: "Immobilie kaufen", href: "/immobilie-kaufen-kreis-mettmann" },
                { label: "Marktwertermittlung", href: "/marktwertermittlung-kreis-mettmann" },
                { label: "Haus verkaufen", href: "/haus-verkaufen-kreis-mettmann" },
                { label: "Wohnung verkaufen", href: "/wohnung-verkaufen-kreis-mettmann" },
                { label: "Immobilienbewertung", href: "/immobilienbewertung-kreis-mettmann" },
                { label: "Immobilienpreise", href: "/immobilienpreise-kreis-mettmann" },
                { label: "Immobilienmakler", href: "/immobilienmakler-kreis-mettmann" },
              ].map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-gray-400 text-sm hover:text-[#E61D38] transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Ratgeber */}
          <div className="sm:col-span-2 md:col-span-1">
            <Link
              to="/ratgeber"
              className="text-white font-bold uppercase tracking-widest text-xs mb-5 block hover:text-[#E61D38] transition-colors"
            >
              Ratgeber
            </Link>
            {settings.aktuelles_enabled !== false && (
              <Link
                to="/aktuelles"
                className="text-[#E61D38] text-xs font-bold uppercase tracking-wider mb-4 inline-block hover:underline"
                data-testid="footer-aktuelles-link"
              >
                Aktuelles
              </Link>
            )}
            <ul className="space-y-3">
              {[
                { label: "Ablauf Verkauf", href: "/wie-laeuft-immobilienverkauf" },
                { label: "Kosten & Steuern", href: "/immobilie-verkaufen-kosten-und-steuern" },
                { label: "Notartermin", href: "/notartermin-immobilienverkauf" },
                { label: "Energieausweis", href: "/energieausweis-immobilienverkauf" },
                { label: "Finanzierung", href: "/immobilienfinanzierung-kreis-mettmann" },
                { label: "Wohnen im Alter", href: "/wohnen-im-alter-kreis-mettmann" },
                { label: "Scheidung", href: "/immobilie-verkaufen-bei-scheidung" },
                { label: "Familiäre Veränderungen", href: "/immobilie-bei-familiaeren-veraenderungen" },
                { label: "Pflegebedürftigkeit", href: "/immobilie-bei-pflegebeduerftigkeit" },
                { label: "Umzug ins Ausland", href: "/immobilie-verkaufen-umzug-ausland" },
              ].map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-gray-400 text-sm hover:text-[#E61D38] transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Städte */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-5">
              Städte
            </h4>
            <ul className="space-y-3">
              {cityConfig.districts.map((d) => (
                <li key={d.slug}>
                  <Link
                    to={`/immobilie-verkaufen-${d.slug}`}
                    className="text-gray-400 text-sm hover:text-[#E61D38] transition-colors"
                  >
                    {d.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/ratgeber"
              className="text-[#E61D38] text-xs font-bold uppercase tracking-wider mt-4 inline-block hover:underline"
              data-testid="footer-alle-ratgeber"
            >
              Alle Ratgeber ansehen
            </Link>

          {/* Kontakt */}
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-5">
              Kontakt
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={14} className="text-[#E61D38] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm leading-relaxed">
                  {cityConfig.address.street}
                  <br />
                  {cityConfig.address.postalCode} {cityConfig.address.city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={14} className="text-[#E61D38] flex-shrink-0" />
                <a
                  href={`tel:${cityConfig.phone}`}
                  className="text-gray-400 text-sm hover:text-[#E61D38] transition-colors"
                  data-testid="footer-phone"
                >
                  {cityConfig.phoneDisplay}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle size={14} className="text-[#E61D38] flex-shrink-0" />
                <a
                  href={`https://wa.me/${cityConfig.whatsapp}`}
                  className="text-gray-400 text-sm hover:text-[#E61D38] transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                >
                  WhatsApp
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-[#E61D38] flex-shrink-0" />
                <a
                  href={`mailto:${cityConfig.email}`}
                  className="text-gray-400 text-xs hover:text-[#E61D38] transition-colors break-all"
                  data-testid="footer-email"
                >
                  {cityConfig.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 py-5 pb-16 sm:pb-5">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-gray-600 text-xs">
            © {year} {cityConfig.fullCompanyName}. Alle Rechte vorbehalten.
          </p>
          <div className="flex items-center gap-6 mr-0 sm:mr-14">
            <Link
              to="/impressum"
              className="text-gray-500 text-xs hover:text-gray-300 transition-colors"
              data-testid="footer-impressum"
            >
              Impressum
            </Link>
            <Link
              to="/datenschutz"
              className="text-gray-500 text-xs hover:text-gray-300 transition-colors"
              data-testid="footer-datenschutz"
            >
              Datenschutz
            </Link>
            <Link
              to="/admin"
              className="text-gray-700 hover:text-gray-500 transition-colors"
              aria-label="Admin"
              data-testid="footer-admin"
            >
              <Lock size={11} />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
